import type React from "react"

import type { MetricIdentity, MetricThresholdSource } from "@/metrics/catalog"
import {
  calculateBinaryHeaderPercent,
  calculateBinaryPassRate,
  hasBinaryUnknowns,
  resolveBinaryTargetCondition,
} from "@/metrics/metric-display"
import type { MetricRecord } from "@/metrics/runtime"

import type { Annotation } from "@/types/api/Annotation"
import type { TestCaseRecord } from "@/types/api/TestCaseRecord"
import type { JsonObject } from "@/types/json"
import type { MetricMetadata } from "@/types/metrics/metadata"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"
import type { AttributeContext } from "@/utils/backend-mimics/replace_with_attributes"

export interface MetricFilterAdapter {
  id: string
  metricScale?: number
  getHighlightValueForMetricOutput: (
    metricOutput: RunMetricOutput,
  ) => number | string | null
}

export interface MetricAnnotationComparator {
  annotationMatchesMetricOutput: (
    annotation: Annotation,
    metricOutput: {
      output_type: string
      float_value: number | null
      string_value: string | null
      set_value: string[] | null
      result_json?: JsonObject | null
    },
  ) => boolean
}

export interface MetricHumanFeedbackAdapter {
  id: string
  getKeyboardShortcutHints: MetricRecord["getKeyboardShortcutHints"]
  renderHumanFeedbackValueButton: MetricRecord["renderHumanFeedbackValueButton"]
}

export type MetricAutoAddRuleSource = Pick<
  MetricIdentity,
  | "id"
  | "metric_name"
  | "metric_display_name"
  | "metric_manager"
  | "output_type"
> & {
  metric_metadata?: Pick<
    MetricMetadata,
    "additional_information" | "metadata_field_type"
  > | null
  getCategoricalOptions?: () => string[]
}

export type MetricReviewBaseTestCase = Pick<
  TestCaseRecord,
  "input_str" | "expected_behaviors" | "expected_output_json"
>

export interface MetricAssignmentReviewAdapter extends MetricHumanFeedbackAdapter {
  metric_name: string
  metric_display_name: string
  metric_metadata: Pick<MetricMetadata, "question">
  current_version_id?: string | null
  current_version_number?: number | null
  hasCustomVotingUI?: boolean
  hasHumanReviewFallback?: boolean
  hasDeepDiveDetail?: boolean
  ownsHumanReviewNotes?: boolean
  /** Values are saved by the per-message, per-segment or audio-region editor, not the card. */
  reviewsOutsideCard?: boolean
  /** The card has no value input, so reviewer notes sit on the collapsed card. */
  hasNotesOnCard?: boolean
  statesResultInCardDetail: MetricRecord["statesResultInCardDetail"]
  renderHumanReviewDetail: MetricRecord["renderHumanReviewDetail"]
  annotationMatchesMetricOutput: MetricRecord["annotationMatchesMetricOutput"]
  getDeepDiveSections: (
    metricOutput: RunMetricOutput,
    baseTestCase: MetricReviewBaseTestCase,
  ) => { label: string; content: React.ReactNode }[] | null
  /**
   * Human Review's "Question" source: the metric's CURRENT question with any
   * parameter substitution applied. Deep dive does NOT use this — it shows
   * whatever question the run was executed with (via getDeepDiveSections).
   */
  renderCurrentQuestion: (
    baseTestCase: MetricReviewBaseTestCase,
    callContext?: AttributeContext,
  ) => React.ReactNode | null
  renderAssignmentsTableAIResultCell: (
    simulationOutputRow: RunRowLike,
    metricId: string,
  ) => React.ReactNode
  renderDeepDive: (
    metricOutput: RunMetricOutput,
    baseTestCase: MetricReviewBaseTestCase,
    dispatchMessageSelect?: (messageIndex: number | null) => void,
  ) => React.ReactNode
  renderDeepDiveAnnotationsPanel: MetricRecord["renderDeepDiveAnnotationsPanel"]
  renderDeepDivePanel: MetricRecord["renderDeepDivePanel"]
}

export interface MetricRenderAdapter
  extends MetricIdentity, MetricThresholdSource, MetricFilterAdapter {
  metric_metadata?:
    | (NonNullable<MetricThresholdSource["metric_metadata"]> &
        Pick<MetricMetadata, "automatic_expected_behavior">)
    | null
  getComparedOverviewNode: MetricRecord["getComparedOverviewNode"]
  getExplanationTable: MetricRecord["getExplanationTable"]
  getHeaderValue: MetricRecord["getHeaderValue"]
  getOverviewNode: MetricRecord["getOverviewNode"]
  getOutputVersionStatus: MetricRecord["getOutputVersionStatus"]
  renderBadge: MetricRecord["renderBadge"]
  renderComparedInDepthRunReview: MetricRecord["renderComparedInDepthRunReview"]
  renderGroupedSimulationCard: MetricRecord["renderGroupedSimulationCard"]
  renderInDepthRunReview: MetricRecord["renderInDepthRunReview"]
  calculateBinaryHeaderPercent: (
    metricOutputs: RunMetricOutput[],
    includeUnknowns: boolean,
  ) => string | null
  calculateBinaryPassRate: (
    metricOutputs: RunMetricOutput[],
    includeUnknowns: boolean,
  ) => number | null
  hasBinaryUnknowns: (metricOutputs: RunMetricOutput[]) => boolean
  invertYesNoColors?: boolean
}

type MetricLookupInput<T> = Record<string, T | null | undefined> | undefined

interface CategoricalOptionsProvider {
  getCategoricalOptions: () => string[]
}

interface BinaryAggregateOutputsProvider {
  getBinaryAggregateOutputs: (
    metricOutputs: RunMetricOutput[],
  ) => RunMetricOutput[]
}

function hasBinaryAggregateOutputsProvider(
  metric: MetricRecord,
): metric is MetricRecord & BinaryAggregateOutputsProvider {
  return (
    "getBinaryAggregateOutputs" in metric &&
    typeof metric.getBinaryAggregateOutputs === "function"
  )
}

interface BinaryAggregateOverviewRecordAdapter extends MetricRecord {
  invertYesNoColors: boolean
  supportsBinaryAggregateOverview: true
}

function hasCategoricalOptionsProvider(
  metric: MetricRecord,
): metric is MetricRecord & CategoricalOptionsProvider {
  return (
    "getCategoricalOptions" in metric &&
    typeof metric.getCategoricalOptions === "function"
  )
}

function supportsBinaryAggregateOverview(
  metric: MetricRecord,
): metric is BinaryAggregateOverviewRecordAdapter {
  return (
    "supportsBinaryAggregateOverview" in metric &&
    metric.supportsBinaryAggregateOverview === true
  )
}

export function buildMetricFilterAdapters(
  metricRecords: MetricLookupInput<MetricRecord>,
): Record<string, MetricFilterAdapter> {
  const result: Record<string, MetricFilterAdapter> = {}

  for (const metric of Object.values(metricRecords || {})) {
    if (!metric?.id) {
      continue
    }

    const metricScale =
      "metricScale" in metric && typeof metric.metricScale === "number"
        ? metric.metricScale
        : undefined

    result[metric.id] = {
      id: metric.id,
      metricScale,
      getHighlightValueForMetricOutput:
        metric.getHighlightValueForMetricOutput.bind(metric),
    }
  }

  return result
}

export function buildMetricRenderAdapters(
  metricRecords: MetricLookupInput<MetricRecord>,
): Record<string, MetricRenderAdapter> {
  const result: Record<string, MetricRenderAdapter> = {}

  for (const metric of Object.values(metricRecords || {})) {
    if (!metric?.id) {
      continue
    }

    const filterAdapter = buildMetricFilterAdapters({ [metric.id]: metric })[
      metric.id
    ]
    const getBinaryAggregateOutputs = hasBinaryAggregateOutputsProvider(metric)
      ? metric.getBinaryAggregateOutputs.bind(metric)
      : (metricOutputs: RunMetricOutput[]) => metricOutputs

    result[metric.id] = {
      id: metric.id,
      metric_name: metric.metric_name,
      metric_display_name: metric.metric_display_name,
      metric_manager: metric.metric_manager,
      output_type: metric.output_type,
      category: metric.category,
      description: metric.description,
      metric_metadata: metric.metric_metadata
        ? {
            target_condition: metric.metric_metadata.target_condition,
            ...(metric.metric_metadata.automatic_expected_behavior !==
              undefined && {
              automatic_expected_behavior:
                metric.metric_metadata.automatic_expected_behavior,
            }),
          }
        : null,
      organization_threshold: metric.organization_threshold ?? null,
      metricScale: filterAdapter.metricScale,
      getHighlightValueForMetricOutput:
        filterAdapter.getHighlightValueForMetricOutput,
      getComparedOverviewNode: metric.getComparedOverviewNode.bind(metric),
      getExplanationTable: metric.getExplanationTable.bind(metric),
      getHeaderValue: metric.getHeaderValue.bind(metric),
      getOverviewNode: metric.getOverviewNode.bind(metric),
      getOutputVersionStatus: metric.getOutputVersionStatus.bind(metric),
      renderBadge: metric.renderBadge.bind(metric),
      renderComparedInDepthRunReview:
        metric.renderComparedInDepthRunReview.bind(metric),
      renderGroupedSimulationCard:
        metric.renderGroupedSimulationCard.bind(metric),
      renderInDepthRunReview: metric.renderInDepthRunReview.bind(metric),
      calculateBinaryHeaderPercent: (metricOutputs, includeUnknowns) => {
        if (!supportsBinaryAggregateOverview(metric)) {
          return null
        }

        return calculateBinaryHeaderPercent(
          getBinaryAggregateOutputs(metricOutputs),
          includeUnknowns,
          resolveBinaryTargetCondition(metric, metric.invertYesNoColors),
        )
      },
      calculateBinaryPassRate: (metricOutputs, includeUnknowns) =>
        calculateBinaryPassRate(
          getBinaryAggregateOutputs(metricOutputs),
          includeUnknowns,
          resolveBinaryTargetCondition(metric),
        ),
      hasBinaryUnknowns: (metricOutputs) =>
        supportsBinaryAggregateOverview(metric) &&
        hasBinaryUnknowns(getBinaryAggregateOutputs(metricOutputs)),
      ...(supportsBinaryAggregateOverview(metric) && {
        invertYesNoColors: metric.invertYesNoColors,
      }),
    }
  }

  return result
}

export function buildMetricAnnotationComparators(
  metricRecords: MetricLookupInput<MetricRecord>,
): Record<string, MetricAnnotationComparator> {
  const result: Record<string, MetricAnnotationComparator> = {}

  for (const metric of Object.values(metricRecords || {})) {
    if (!metric?.id) {
      continue
    }

    result[metric.id] = {
      annotationMatchesMetricOutput:
        metric.annotationMatchesMetricOutput.bind(metric),
    }
  }

  return result
}

export function buildMetricAutoAddRuleSources(
  metricRecords: MetricLookupInput<MetricRecord>,
): Record<string, MetricAutoAddRuleSource> {
  const result: Record<string, MetricAutoAddRuleSource> = {}

  for (const metric of Object.values(metricRecords || {})) {
    if (!metric?.id) {
      continue
    }

    const projection: MetricAutoAddRuleSource = {
      id: metric.id,
      metric_name: metric.metric_name,
      metric_display_name: metric.metric_display_name,
      metric_manager: metric.metric_manager,
      output_type: metric.output_type,
      metric_metadata: metric.metric_metadata
        ? {
            additional_information:
              metric.metric_metadata.additional_information,
            metadata_field_type: metric.metric_metadata.metadata_field_type,
          }
        : null,
    }
    if (hasCategoricalOptionsProvider(metric)) {
      projection.getCategoricalOptions =
        metric.getCategoricalOptions.bind(metric)
    }
    result[metric.id] = projection
  }

  return result
}

export function buildMetricAssignmentReviewAdapters(
  metricRecords: MetricLookupInput<MetricRecord>,
): Record<string, MetricAssignmentReviewAdapter> {
  const result: Record<string, MetricAssignmentReviewAdapter> = {}

  for (const metric of Object.values(metricRecords || {})) {
    if (!metric?.id) {
      continue
    }

    const reviewsOutsideCard =
      metric.getPerMessageReviewConfig() !== null ||
      metric.getPerSegmentReviewConfig() !== null ||
      metric.getAudioRegionReviewConfig() !== null

    result[metric.id] = {
      id: metric.id,
      metric_name: metric.metric_name,
      metric_display_name: metric.metric_display_name,
      metric_metadata: { question: metric.metric_metadata?.question },
      current_version_id: metric.current_version_id,
      current_version_number: metric.current_version_number,
      hasCustomVotingUI: metric.hasCustomVotingUI,
      hasHumanReviewFallback: metric.hasHumanReviewFallback,
      hasDeepDiveDetail: metric.hasDeepDiveDetail,
      ownsHumanReviewNotes: metric.ownsHumanReviewNotes,
      reviewsOutsideCard,
      hasNotesOnCard: !metric.supportsHumanReview && !reviewsOutsideCard,
      statesResultInCardDetail: metric.statesResultInCardDetail.bind(metric),
      renderHumanReviewDetail: metric.renderHumanReviewDetail.bind(metric),
      annotationMatchesMetricOutput:
        metric.annotationMatchesMetricOutput.bind(metric),
      getDeepDiveSections: (metricOutput, testCase) =>
        metricOutput && !metric.hasValidDeepDiveData(metricOutput)
          ? null
          : metric.getDeepDiveSections(metricOutput, testCase),
      renderCurrentQuestion: metric.renderCurrentQuestion.bind(metric),
      getKeyboardShortcutHints: metric.getKeyboardShortcutHints.bind(metric),
      renderAssignmentsTableAIResultCell:
        metric.renderAssignmentsTableAIResultCell.bind(metric),
      renderDeepDive: metric.renderDeepDive.bind(metric),
      renderDeepDiveAnnotationsPanel:
        metric.renderDeepDiveAnnotationsPanel.bind(metric),
      renderDeepDivePanel: (
        notesNode,
        metricOutput,
        onMessageSelect,
        context,
      ) =>
        metricOutput && !metric.hasValidDeepDiveData(metricOutput)
          ? null
          : metric.renderDeepDivePanel(
              notesNode,
              metricOutput,
              onMessageSelect,
              context,
            ),
      renderHumanFeedbackValueButton:
        metric.renderHumanFeedbackValueButton.bind(metric),
    }
  }

  return result
}
